import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import React from 'react';
import { useTranslation } from 'react-i18next';
import logo from '../assets/SVG/Clinic Logo.png';
// import logo from '../../../../../assets/SVG/Clinic Logo.png';
import { Box, Typography, SxProps, Theme } from '@mui/material';

export async function generatePDF(element: HTMLElement, filename: string) {
  const elementWidth = element.offsetWidth;
  const elementHeight = element.offsetHeight;
  const pxToMm = (px: number) => px * 0.264583;
  const pdfWidth = pxToMm(elementWidth);
  const pdfHeight = pxToMm(elementHeight);

  // Temporarily adjust footer for PDF generation
  const footer = element.querySelector('.report-footer-timestamp') as HTMLElement;
  const originalStyles = footer ? {
    position: footer.style.position,
    bottom: footer.style.bottom,
    right: footer.style.right,
    display: footer.style.display,
    textAlign: footer.style.textAlign,
    paddingTop: footer.style.paddingTop,
  } : null;

  if (footer) {
    footer.style.position = 'relative';
    footer.style.bottom = 'auto';
    footer.style.right = 'auto';
    footer.style.display = 'block';
    footer.style.textAlign = 'right';
    footer.style.paddingTop = '10px';
  }

  // Lower scale for smaller file, but still good quality
  const canvas = await html2canvas(element, {
    scale: 1.2,
    useCORS: true,
  });

  // Restore original styles after canvas capture
  if (footer && originalStyles) {
    footer.style.position = originalStyles.position;
    footer.style.bottom = originalStyles.bottom;
    footer.style.right = originalStyles.right;
    footer.style.display = originalStyles.display;
    footer.style.textAlign = originalStyles.textAlign;
    footer.style.paddingTop = originalStyles.paddingTop;
  }

  // Use JPEG and lower quality for smaller file size
  const imgData = canvas.toDataURL('image/jpeg', 0.9);
  const pdf = new jsPDF({
    orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
    unit: 'mm',
    format: [pdfWidth, pdfHeight],
    compress: true,
  });
  pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
  pdf.save(`${filename}.pdf`);
}


export const ClinicHeader = () => {
  const { t: tGeneral } = useTranslation('general');
  return (
    <Box sx={{ display: 'flex', height: 120, borderBottom: '0.1px solid #3f3f3f' }}>
      {/* Left Section with Background */}
      <Box
        sx={{
          backgroundColor: '#fdf4f1',
          padding: 2,
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {tGeneral('clinicName')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {tGeneral('clinicAddress')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>{tGeneral('email')}:</strong> ramon.morissette54@gmail.com
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>{tGeneral('mobileNo')}:</strong> 578-778-3291
          </Typography>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ width: '20px', backgroundColor: '#e97c5e' }} />

      {/* Right Section with Logo */}
      <Box
        sx={{
          width: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
          backgroundColor: '#fff',
        }}
      >
        <Box
          component="img"
          src={logo}
          alt={tGeneral('clinicLogo')}
          sx={{ width: 100, height: 'auto', objectFit: 'contain' }}
        />
      </Box>
    </Box>
  );
};


export const InfoGrid: React.FC<{ data: string[][] }> = ({ data }) => {
  const { t: tGeneral } = useTranslation('general');

  // convert "-" placeholders to empty strings and filter out rows that
  // don't contain any meaningful values. This prevents printing unsaved or
  // unavailable data in reports.
  const filteredData = data
    .map((row) => {
      const [label1, value1, label2, value2] = row;
      return [
        label1,
        value1 === '-' ? '' : value1,
        label2,
        value2 === '-' ? '' : value2,
      ];
    })
    .filter(([label1, value1, label2, value2]) => {
      // Section header: only label1 is filled, others are empty
      const isSectionHeader = label1 && !value1 && !label2 && !value2;
      // Keep section headers and rows with meaningful values
      return isSectionHeader || (label1 && value1) || (label2 && value2);
    });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        fontSize: 14,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        backgroundColor: '#fff',
      }}
    >
      {filteredData.map((row, index) => {
        const [label1, value1, label2, value2] = row;
        // Section header: only label1 is filled, others are empty
        const isSectionHeader = label1 && !value1 && !label2 && !value2;
        if (isSectionHeader) {
          return (
            <Box
              key={index}
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: '#f1f1f1',
                borderBottom: index !== filteredData.length - 1 ? '1px solid #e0e0e0' : 'none',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {tGeneral(label1)}
              </Typography>
            </Box>
          );
        }
        // Normal row
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#ffffff',
              borderBottom: index !== filteredData.length - 1 ? '1px solid #e0e0e0' : 'none',
            }}
          >
            {[label1, value1].every(Boolean) && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">
                  <span style={{ color: '#666' }}>{tGeneral(label1)}:</span> <span style={{ color: '#000', fontWeight: 'bold' }}>{value1}</span>
                </Typography>
              </Box>
            )}
            {[label2, value2].every(Boolean) && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">
                  <span style={{ color: '#666' }}>{tGeneral(label2)}:</span> <span style={{ color: '#000', fontWeight: 'bold' }}>{value2}</span>
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export const tableCellStyle: SxProps<Theme> = {
  border: '1px solid #ccc',
};

/**
 * Convert a number to its English words representation (supports up to crores).
 * Used for Amount in Words on invoices/receipts.
 */
export function numberToWords(amount: number): string {
  if (isNaN(amount) || amount < 0) return '';
  if (amount === 0) return 'Zero';

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function convertBelow1000(n: number): string {
    if (n === 0) return '';
    if (n < 20) return ones[n] + ' ';
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '') + ' ';
    return ones[Math.floor(n / 100)] + ' Hundred ' + convertBelow1000(n % 100);
  }

  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);

  let result = '';
  let rem = rupees;

  if (rem >= 10000000) {
    result += convertBelow1000(Math.floor(rem / 10000000)) + 'Crore ';
    rem %= 10000000;
  }
  if (rem >= 100000) {
    result += convertBelow1000(Math.floor(rem / 100000)) + 'Lakh ';
    rem %= 100000;
  }
  if (rem >= 1000) {
    result += convertBelow1000(Math.floor(rem / 1000)) + 'Thousand ';
    rem %= 1000;
  }
  result += convertBelow1000(rem);

  result = result.trim();
  let words = result ? result + ' Rupees' : '';
  if (paise > 0) {
    words += (words ? ' and ' : '') + convertBelow1000(paise).trim() + ' Paise';
  }
  words += ' Only';
  return words.trim();
}

export const ReportFooterTimestamp: React.FC = () => {
  const { t: tGeneral } = useTranslation('general');
  const currentDateTime = React.useMemo(() => new Date().toLocaleString(), []);
  return (
    <Typography
      variant="caption"
      sx={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        color: '#666',
        fontStyle: 'italic',
        mt: 3,
        '@media print': {
          position: 'relative',
          display: 'block',
          textAlign: 'right',
          bottom: 'auto',
          right: 'auto',
          pt: 2,
        },
      }}
      className="report-footer-timestamp"
    >
      {tGeneral('ReportGeneratedOn')}: {currentDateTime}
    </Typography>
  );
};